import { useMemo } from "react";
import { useGetChfarItineraryByIdQuery } from "../reducers/itineraries/api";
import getChfarDataFromItinerary from "../reducers/itineraries/helpers";
import type { ChFARPolicyDetails } from "../types";

/**
 * Custom hook to fetch the Change for Any Reason (ChFAR) policy details.
 *
 * This hook fetches the policy details for the given itinerary and contract IDs,
 * and provides loading and error states for the request.
 *
 * @param {{ itineraryId: string; contractId: string }} params - The itinerary and contract identifiers
 * @param {boolean} [skip=false] - Whether to skip the API request
 * @returns {{ data?: ChFARPolicyDetails; isLoading: boolean; error?: unknown }} - The policy details, loading state, and any error
 */
export const useChfarPolicy = (
  {
    itineraryId,
    contractId,
  }: {
    itineraryId: string;
    contractId: string;
  },
  skip = false
): {
  data?: ChFARPolicyDetails;
  isLoading: boolean;
  error?: unknown;
} => {
  const { data, isLoading, error } = useGetChfarItineraryByIdQuery(
    { itineraryId, contractId },
    {
      skip: skip || itineraryId === "" || contractId === "",
    }
  );

  return useMemo(
    () => ({
      data: data != null ? getChfarDataFromItinerary(data) : data,
      isLoading,
      error,
    }),
    [data, isLoading, error]
  );
};
